import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Avatar,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  AccessTime,
  ArrowForward,
  ArrowBack,
} from '@mui/icons-material';
import { blogPosts, getFormattedContent } from '../data/blogPosts';

const BlogPage = () => {
  const [selectedPost, setSelectedPost] = useState(null);

  // South African-inspired color palette
  const colors = {
    primary: '#1A237E', // Updated to match theme
    secondary: '#007A4D', // Green from SA flag
    accent: '#FFB347', // Warm African sun
    dark: '#222831',
    light: '#F7F7F7',
  };

  const getCategoryColor = (category) => {
    const categoryColors = {
      Innovation: colors.primary,
      Marketing: colors.secondary,
      'E-commerce': colors.accent,
      Design: colors.primary,
      Fintech: colors.secondary,
      Sustainability: colors.accent,
    };
    return categoryColors[category] || colors.dark;
  };

  const BlogPostList = () => (
    <Grid container spacing={4}>
      {blogPosts.map((post, index) => (
        <Grid item xs={12} md={6} key={index}>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            <Card
              sx={{
                height: '100%',
                borderRadius: 4,
                overflow: 'hidden',
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-10px)',
                },
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <CardMedia
                component="img"
                height="240"
                image={post.image}
                alt={post.title}
                sx={{
                  objectFit: 'cover',
                }}
              />
              <CardContent sx={{ p: 4, flexGrow: 1 }}>
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={post.category}
                    sx={{
                      backgroundColor: `${getCategoryColor(post.category)}22`,
                      color: getCategoryColor(post.category),
                      fontWeight: 600,
                    }}
                  />
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: colors.dark,
                    minHeight: 64,
                  }}
                >
                  {post.title}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {post.excerpt}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 'auto',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                      src={post.author.avatar}
                      sx={{ width: 40, height: 40, mr: 2 }}
                    />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600 }}
                      >
                        {post.author.name}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: 'text.secondary',
                          fontSize: '0.875rem',
                        }}
                      >
                        <AccessTime
                          sx={{ fontSize: 16, mr: 0.5 }}
                        />
                        {post.readTime}
                      </Box>
                    </Box>
                  </Box>
                  <Button
                    endIcon={<ArrowForward />}
                    onClick={() => setSelectedPost(post)}
                    sx={{
                      color: colors.primary,
                      '&:hover': {
                        backgroundColor: `${colors.primary}11`,
                      },
                    }}
                  >
                    Read More
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      ))}
    </Grid>
  );

  const BlogPostDetail = ({ post }) => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Button
        startIcon={<ArrowBack />}
        onClick={() => setSelectedPost(null)}
        sx={{ mb: 4, color: colors.primary }}
      >
        Back to Blog List
      </Button>
      <Card sx={{ borderRadius: 4, overflow: 'hidden', mb: 4 }}>
        <CardMedia
          component="img"
          height="400"
          image={post.image}
          alt={post.title}
          sx={{ objectFit: 'cover' }}
        />
      </Card>
      <Box sx={{ mb: 4 }}>
        <Chip
          label={post.category}
          sx={{
            backgroundColor: `${getCategoryColor(post.category)}22`,
            color: getCategoryColor(post.category),
            fontWeight: 600,
            mb: 2,
          }}
        />
        <Typography variant="h2" sx={{ fontWeight: 800, mb: 3 }}>
          {post.title}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 4,
          }}
        >
          <Avatar
            src={post.author.avatar}
            sx={{ width: 48, height: 48, mr: 2 }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {post.author.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {post.author.role} • {post.date} • {post.readTime}
            </Typography>
          </Box>
        </Box>
        <Divider sx={{ mb: 4 }} />
        {getFormattedContent(post.content).map((paragraph, index) => {
          // Check if the paragraph is an image (markdown format)
          const imageMatch = paragraph.match(/!\[(.*?)\]\((.*?)\)/);
          
          if (imageMatch) {
            const [, altText, imageUrl] = imageMatch;
            // Parse size information from alt text if present
            const sizeMatch = altText.match(/size:(\d+)x(\d+)/);
            const customWidth = sizeMatch ? sizeMatch[1] + 'px' : '100%';
            const customHeight = sizeMatch ? sizeMatch[2] + 'px' : 'auto';
            const displayAltText = altText.replace(/size:\d+x\d+\s*/, '').trim();

            return (
              <Box key={index} sx={{ my: 6 }}>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: customWidth,
                    margin: '0 auto',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  }}
                >
                  <CardMedia
                    component="img"
                    image={imageUrl}
                    alt={displayAltText}
                    sx={{
                      width: '100%',
                      height: customHeight,
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.02)',
                      },
                    }}
                  />
                </Box>
                {displayAltText && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      textAlign: 'center',
                      mt: 2,
                      fontStyle: 'italic',
                    }}
                  >
                    {displayAltText}
                  </Typography>
                )}
              </Box>
            );
          }

          return (
            <Typography
              key={index}
              variant="body1"
              sx={{
                mb: 2,
                lineHeight: 1.8,
                color: 'text.secondary',
                ...(paragraph.match(/^[A-Z][\w\s]+$/) && {
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '1.25rem',
                  mt: 4,
                  mb: 2,
                }),
              }}
            >
              {paragraph}
            </Typography>
          );
        })}
      </Box>
    </motion.div>
  );

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* HERO SECTION */}
      <Box
        sx={{
          py: 12,
          background: `linear-gradient(135deg, ${colors.primary}dd, ${colors.secondary}dd)`,
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                mb: 3,
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              Our Blog
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                maxWidth: '800px',
                mx: 'auto',
                fontWeight: 300,
              }}
            >
              Insights and perspectives on African tech, innovation, and digital excellence
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* BLOG CONTENT */}
      <Box
        sx={{
          py: 12,
          backgroundColor: colors.light,
        }}
      >
        <Container maxWidth="lg">
          {selectedPost ? (
            <BlogPostDetail post={selectedPost} />
          ) : (
            <BlogPostList />
          )}
        </Container>
      </Box>

      {/* NEWSLETTER SECTION */}
      {!selectedPost && (
        <Box
          sx={{
            py: 12,
            backgroundColor: colors.dark,
            color: '#fff',
            textAlign: 'center',
          }}
        >
          <Container maxWidth="md">
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
              Stay Updated
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                color: 'rgba(255,255,255,0.8)',
                fontWeight: 300,
              }}
            >
              Subscribe to our newsletter for the latest insights on African tech and innovation
            </Typography>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  px: 6,
                  py: 2,
                  borderRadius: '30px',
                  backgroundColor: colors.primary,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: colors.primary,
                    filter: 'brightness(110%)',
                  },
                }}
              >
                Subscribe Now
              </Button>
            </motion.div>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default BlogPage; 